import { useQuery } from '@apollo/client';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { VersionStatus } from '@risksmart-app/domain/src/types/consts/version-status';
import type { Version_Status_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetDocumentFileDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

import { useGetLatestDocumentFile as useGetLatestDocumentFileTRPC } from './useGetLatestDocumentFileTRPC';

export const useGetLatestDocumentFile = (
  parentDocumentId: string,
  fileId?: string,
  status?: VersionStatus | Version_Status_Enum,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const { addNotification } = useNotifications();

  // Build where clause for GraphQL query
  const buildWhereClause = () => {
    const where: Record<string, unknown> = {
      ParentDocumentId: { _eq: parentDocumentId },
    };

    // Add fileId filter if provided and not 'latest'
    if (fileId && fileId !== 'latest') {
      where.Id = { _eq: fileId };
    }

    // Add status filter if provided
    if (status) {
      where.Status = { _eq: status };
    }

    return where;
  };

  const {
    data: graphqlData,
    loading: graphqlLoading,
    refetch: graphqlRefetch,
    error: graphqlError,
  } = useQuery(GetDocumentFileDocument, {
    variables: {
      where: buildWhereClause(),
    },
    fetchPolicy: 'no-cache',
    skip: trpcEnabled || skip, // Skip GraphQL query when TRPC is enabled
    onError: (error) => {
      if (!trpcEnabled) {
        addNotification({
          type: 'error',
          content: <>{error.message}</>,
        });
      }
    },
  });

  const {
    data: trpcData,
    loading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useGetLatestDocumentFileTRPC(
    parentDocumentId,
    fileId,
    status as VersionStatus,
    skip
  );

  // Return appropriate data based on feature flag
  if (trpcEnabled) {
    return {
      loading: trpcLoading,
      data: trpcData,
      refetch: () => trpcRefetch(),
      error: trpcError,
    };
  }

  return {
    loading: graphqlLoading,
    data: graphqlData,
    refetch: () => graphqlRefetch(),
    error: graphqlError,
  };
};
